import {WarehouseColumnData, WarehouseRelationshipData} from 'insights/warehouse/warehouseModelsDataLayout';

import {
  DWD_HORIZONTAL_GUTTER_SIZE,
  DWD_MAX_COLLAPSED_TABLE_HEIGHT,
  DWD_TABLE_WIDTH,
  DWD_VERTICAL_GUTTER_SIZE,
} from './diagramLayoutConstants';
import {columnToPixel, columnVerticalOffset, pixelToCell} from './diagramLayoutHelpers';
import {
  DiagramGridCell,
  DiagramLayout,
  HorizontalSide,
  VerticalSide,
  WarehouseRelationshipPath,
} from './diagramLayoutTypes';

export function cellColumnIsIn(layout: DiagramLayout, column: WarehouseColumnData): DiagramGridCell {
  return pixelToCell(columnToPixel(layout, column, VerticalSide.LEFT));
}

/**
 * Generates a list of points connecting the source column and the target column of the input relationship.
 *
 * Additionally returns which side of the source column the path "exits" from,
 * and the side of the target column the path enters.
 *
 * For columns shown in the same grid row and within at most one grid column from each other, four points
 * are sufficient for connecting the source & target, as there is no need for the path to go up/down to
 * a horizontal gutter.
 *
 * For all other source/target column locations, a 6 point path is returned, though some points may be
 * duplicates.
 *
 * See the below image for a visual representation of the 4/6 point paths:
 * https://user-images.githubusercontent.com/10499901/88408414-84395500-cda1-11ea-8ea5-c311a990370c.png
 */
export default function getRelationshipPath(
  layout: DiagramLayout,
  relationship: WarehouseRelationshipData
): WarehouseRelationshipPath {
  // Columns may not be visually located in the same cell that their containing tables are in.
  // For example, the 10th row in a table that has been vertically expanded is probably rendered in the
  // grid cell beneath the table's top grid cell.
  const sourceCell = cellColumnIsIn(layout, relationship.source);
  const targetCell = cellColumnIsIn(layout, relationship.target);

  const deltaX = targetCell.x - sourceCell.x;
  const deltaY = targetCell.y - sourceCell.y;

  // verticalSide[Start|End] determine which vertical side (left or right) of the source/target table
  // the relationship path enters/exits. For example, for a relationship with the source to the left
  // of the target, the relationship starts going out of the right side of the source and ends going
  // into the left side of the target.
  let verticalSideStart: VerticalSide;
  let verticalSideEnd: VerticalSide;

  if (deltaX === 0) {
    verticalSideStart = VerticalSide.LEFT;
    verticalSideEnd = VerticalSide.LEFT;
  } else if (deltaX < 0) {
    verticalSideStart = VerticalSide.LEFT;
    verticalSideEnd = VerticalSide.RIGHT;
  } else {
    verticalSideStart = VerticalSide.RIGHT;
    verticalSideEnd = VerticalSide.LEFT;
  }

  // First pixel coordinate in the relationship path.
  const sourcePixel = columnToPixel(layout, relationship.source, verticalSideStart);
  // Last pixel coordinate in the relationship path.
  const targetPixel = columnToPixel(layout, relationship.target, verticalSideEnd);

  // Move from sourcePixel over left/right by half of the vertical gutter size based on verticalSideStart.
  const secondPixel = {
    x: sourcePixel.x + ((verticalSideStart === VerticalSide.LEFT ? -1 : 1) * DWD_VERTICAL_GUTTER_SIZE) / 2,
    y: sourcePixel.y,
  };

  // Move from targetPixel over left/right by half of the vertical gutter size based on verticalSideEnd.
  const penultimatePixel = {
    x: targetPixel.x + ((verticalSideEnd === VerticalSide.LEFT ? -1 : 1) * DWD_VERTICAL_GUTTER_SIZE) / 2,
    y: targetPixel.y,
  };

  // Only the four points computed thus far are required if the target table is a) in the same column as,
  // and b) no more than one row away from, the source.
  if (deltaY === 0 && deltaX >= -1 && deltaX <= 1) {
    return {
      points: [sourcePixel, secondPixel, penultimatePixel, targetPixel],
      sourceExitSide: verticalSideStart,
      targetEntranceSide: verticalSideEnd,
    };
  }

  // horizontalSide[Start|End] determine which horizontal side (top or bottom) of the
  // source/target table the relationship path enters/exits. For example, for a relationship with
  // the source below the target, the relationship path should go beyond the top of the source table
  // then proceed to the bottom of the target table.
  let horizontalSideStart: HorizontalSide;
  let horizontalSideEnd: HorizontalSide;
  if (deltaY === 0) {
    horizontalSideStart = HorizontalSide.TOP;
    horizontalSideEnd = HorizontalSide.TOP;
  } else if (deltaY < 0) {
    horizontalSideStart = HorizontalSide.TOP;
    horizontalSideEnd = HorizontalSide.BOTTOM;
  } else {
    horizontalSideStart = HorizontalSide.BOTTOM;
    horizontalSideEnd = HorizontalSide.TOP;
  }

  // Pixel distance required for the path to "climb" up/down to reach the correct horizontal
  // gutter before traveling to its target.
  const offsetToClosestHorizontalGutter =
    horizontalSideStart === HorizontalSide.TOP
      ? -columnVerticalOffset(relationship.source) - DWD_HORIZONTAL_GUTTER_SIZE / 2
      : DWD_MAX_COLLAPSED_TABLE_HEIGHT -
        columnVerticalOffset(relationship.source) +
        DWD_HORIZONTAL_GUTTER_SIZE / 2;

  // Integer number of horizontal gutters the relationship path needs to
  // "climb" up/down to reach target cell.
  const deltaHorizontalGutters =
    (horizontalSideEnd === HorizontalSide.TOP ? targetCell.y - 1 : targetCell.y) -
    (horizontalSideStart === HorizontalSide.TOP ? sourceCell.y - 1 : sourceCell.y);

  // Pixel distance required for the path to climb up/down to reach the target horizontal gutter.
  const offsetToEndHorizontalGutter =
    deltaHorizontalGutters * (DWD_MAX_COLLAPSED_TABLE_HEIGHT + DWD_HORIZONTAL_GUTTER_SIZE);

  // Move from secondPixel up/down the number of pixels required to get to the target's horizontal gutter.
  const thirdPixel = {
    x: secondPixel.x,
    y: secondPixel.y + offsetToClosestHorizontalGutter + offsetToEndHorizontalGutter,
  };

  // Integer number of vertical gutters the relationship path needs to
  // "walk" left/right to reach target cell.
  const deltaVerticalGutters =
    (verticalSideEnd === VerticalSide.LEFT ? targetCell.x - 1 : targetCell.x) -
    (verticalSideStart === VerticalSide.LEFT ? sourceCell.x - 1 : sourceCell.x);

  // Pixel distance required for the path to walk left/right to get to the target vertical gutter.
  const offsetToEndVerticalGutter = deltaVerticalGutters * (DWD_TABLE_WIDTH + DWD_VERTICAL_GUTTER_SIZE);
  const fourthPixel = {
    x: thirdPixel.x + offsetToEndVerticalGutter,
    y: thirdPixel.y,
  };

  return {
    points: [sourcePixel, secondPixel, thirdPixel, fourthPixel, penultimatePixel, targetPixel],
    sourceExitSide: verticalSideStart,
    targetEntranceSide: verticalSideEnd,
  };
}
